import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { config } from '../config';

interface FormConfig {
  toEmail: string;
  subject: string;
  formName?: string;
  accentColor?: string;
}

interface Form {
  formId: string;
  config: FormConfig;
}

interface BrowserEmailProps {
  companyId: string;
  apiBaseUrl: string;
  token: string;
}

const BrowserEmail: React.FC<BrowserEmailProps> = ({ companyId, apiBaseUrl, token }) => {
  const [forms, setForms] = useState<Record<string, FormConfig>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedIntegrationCode, setExpandedIntegrationCode] = useState<Record<string, boolean>>({});

  // Form state for create/edit
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    formId: '',
    toEmail: '',
    subject: '',
    formName: '',
    accentColor: '#667eea'
  });

  // Load forms on component mount
  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async (skipCache = false) => {
    try {
      console.log('🔄 Loading forms for companyId:', companyId);

      // Check cache first (5-minute cache)
      const cacheKey = `forms_${companyId}`;
      const cached = localStorage.getItem(cacheKey);
      if (!skipCache && cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < 5 * 60 * 1000) { // 5 minutes
            console.log('✅ Using cached forms (age: ' + Math.round(age / 1000) + 's)');
            setForms(data.forms || {});
            return;
          }
        } catch (e) {
          console.log('Cache invalid, fetching fresh data');
        }
      }

      console.log('📡 API URL:', `${apiBaseUrl}/mail-handler/get-forms/${companyId}`);
      const startTime = Date.now();

      setLoading(true);
      setError(null);

      const response = await fetch(`${apiBaseUrl}/mail-handler/get-forms/${companyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const endTime = Date.now();
      console.log(`⏱️ API call took ${endTime - startTime}ms`);

      if (!response.ok) {
        console.error('❌ API call failed:', response.status, response.statusText);
        throw new Error('Failed to load forms');
      }

      const data = await response.json();
      console.log('✅ Forms loaded:', Object.keys(data.forms || {}).length, 'forms');

      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));

      setForms(data.forms || {});
    } catch (err: any) {
      console.error('❌ Error loading forms:', err);
      setError(err.message || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrEditForm = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        formId: editingFormId || formData.formId,
        config: {
          toEmail: formData.toEmail,
          subject: formData.subject,
          formName: formData.formName || formData.formId,
          accentColor: formData.accentColor
        }
      };

      const response = await fetch(`${apiBaseUrl}/mail-handler/add-form/${companyId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save form');
      }

      setSuccessMessage(editingFormId ? 'Form updated successfully!' : 'Form created successfully!');
      setIsFormDialogOpen(false);
      resetFormData();
      await loadForms(true); // Skip cache after creating/editing

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save form');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteForm = async (formId: string) => {
    if (!window.confirm(`Are you sure you want to delete the form "${formId}"?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiBaseUrl}/mail-handler/delete-form/${companyId}/${formId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete form');
      }

      setSuccessMessage('Form deleted successfully!');
      await loadForms(true); // Skip cache after deleting

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete form');
    } finally {
      setLoading(false);
    }
  };

  const handleEditForm = (formId: string, config: FormConfig) => {
    setEditingFormId(formId);
    setFormData({
      formId: formId,
      toEmail: config.toEmail,
      subject: config.subject,
      formName: config.formName || formId,
      accentColor: config.accentColor || '#667eea'
    });
    setIsFormDialogOpen(true);
  };

  const resetFormData = () => {
    setEditingFormId(null);
    setFormData({
      formId: '',
      toEmail: '',
      subject: '',
      formName: '',
      accentColor: '#667eea'
    });
  };

  const handleCloseDialog = () => {
    setIsFormDialogOpen(false);
    resetFormData();
  };

  const generateIntegrationCode = (formId: string) => {
    const sdkUrl = `${config.FRONTEND_BASE_URL}/dashzap-forms.js`;

    return `<!-- 1. Load Dashzap Forms SDK -->
<script src="${sdkUrl}"></script>

<!-- 2. Add your form (use id="${formId}" to match your Dashzap form) -->
<form id="${formId}">
  <input type="text" name="name" placeholder="Your Name" required />
  <input type="email" name="email" placeholder="Your Email" required />
  <textarea name="message" placeholder="Your Message" required></textarea>
  <button type="submit">Send</button>
</form>

<!-- 3. Initialize form handler -->
<script>
new DashzapForms({
  formId: '${formId}',
  companyId: '${companyId}',
  callbacks: {
    onSuccess: function(data) {
      alert('Message sent successfully!');
    },
    onError: function(error) {
      alert('Failed to send message. Please try again.');
    }
  }
});
</script>`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Code copied to clipboard!');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Browser Email</h2>
          <p className="text-gray-600 mt-1">Send emails from your website using our REST API</p>
        </div>
        <Button onClick={() => setIsFormDialogOpen(true)}>
          + Create Form
        </Button>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Forms List */}
      {loading && Object.keys(forms).length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
              <p className="text-gray-500">Loading forms...</p>
              <p className="text-xs text-gray-400 mt-1">Fetching from API: {apiBaseUrl}/mail-handler/get-forms/{companyId}</p>
              <p className="text-xs text-gray-400 mt-1">Check browser console for timing details</p>
            </div>
          </CardContent>
        </Card>
      ) : Object.keys(forms).length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <p className="text-gray-500 mb-4">No forms configured yet</p>
              <Button onClick={() => setIsFormDialogOpen(true)}>
                Create Your First Form
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {Object.entries(forms).map(([formId, config]) => (
            <Card key={formId}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{config.formName || formId}</CardTitle>
                    <CardDescription>Form ID: {formId}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditForm(formId, config)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteForm(formId)}
                      disabled={loading}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Form Configuration */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Recipient Email</p>
                    <p className="font-medium">{config.toEmail}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Subject</p>
                    <p className="font-medium">{config.subject}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Accent Color</p>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: config.accentColor }}
                      />
                      <p className="font-medium">{config.accentColor}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-500">Rate Limit</p>
                    <p className="font-medium">500 emails/day</p>
                  </div>
                </div>

                {/* Integration Code - Collapsible */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <button
                      onClick={() => setExpandedIntegrationCode(prev => ({ ...prev, [formId]: !prev[formId] }))}
                      className="flex items-center gap-2 text-sm font-medium hover:underline"
                    >
                      <span>{expandedIntegrationCode[formId] ? '▼' : '▶'}</span>
                      <Label className="cursor-pointer">Integration Code</Label>
                    </button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(generateIntegrationCode(formId))}
                    >
                      Copy Code
                    </Button>
                  </div>
                  {expandedIntegrationCode[formId] && (
                    <pre className="bg-gray-50 p-4 rounded text-xs overflow-x-auto border">
                      <code>{generateIntegrationCode(formId)}</code>
                    </pre>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Form Dialog */}
      {isFormDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <CardHeader>
              <CardTitle>{editingFormId ? 'Edit Form' : 'Create New Form'}</CardTitle>
              <CardDescription>
                {editingFormId
                  ? 'Update your form configuration'
                  : 'Configure a new email form for your website'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!editingFormId && (
                <div className="space-y-2">
                  <Label htmlFor="formId">Form ID *</Label>
                  <Input
                    id="formId"
                    value={formData.formId}
                    onChange={(e) => setFormData({ ...formData, formId: e.target.value })}
                    placeholder="e.g., contact-form"
                    disabled={!!editingFormId}
                  />
                  <p className="text-xs text-gray-500">
                    Alphanumeric characters, hyphens, and underscores only
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="toEmail">Recipient Email *</Label>
                <Input
                  id="toEmail"
                  type="email"
                  value={formData.toEmail}
                  onChange={(e) => setFormData({ ...formData, toEmail: e.target.value })}
                  placeholder="recipient@example.com"
                />
                <p className="text-xs text-gray-500">
                  Where form submissions will be sent
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="New Form Submission"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="formName">Form Name</Label>
                <Input
                  id="formName"
                  value={formData.formName}
                  onChange={(e) => setFormData({ ...formData, formName: e.target.value })}
                  placeholder="Contact Form"
                />
                <p className="text-xs text-gray-500">
                  Optional: Display name for the form
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accentColor">Accent Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="accentColor"
                    type="color"
                    value={formData.accentColor}
                    onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    value={formData.accentColor}
                    onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                    placeholder="#667eea"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Color used in email template
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleCreateOrEditForm}
                  disabled={loading || !formData.toEmail || !formData.subject || (!editingFormId && !formData.formId)}
                  className="flex-1"
                >
                  {loading ? 'Saving...' : editingFormId ? 'Update Form' : 'Create Form'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCloseDialog}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BrowserEmail;
